/**
 * One hook holds the whole map-screen state: fields, the polygon being drawn, runs, the
 * batch queue and ground truth. It is deliberately not a context provider — App owns it
 * and passes slices down, so every component's inputs are visible in its props.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import * as api from "../api/client";
import { ApiError } from "../api/client";
import type {
  Field,
  FieldMarker,
  FieldMarkerInput,
  FieldMarkerPatch,
  FieldPatch,
  FieldPhoto,
  FieldPhotoPatch,
  FieldUse,
  GeoJsonPolygon,
  GroundTruth,
  GroundTruthInput,
  PhotoPlacement,
  PhotoPlacementCause,
  ProgressEvent,
  Run,
  RunFlags,
  ServerFlags,
} from "../api/types";
import { T } from "../strings";
import { areaHectares, type ImportedFeature } from "../lib/geo";
import {
  POLICY_MARKER_MAX_DISTANCE_M,
  POLICY_MARKERS_PER_FIELD,
  POLICY_PHOTO_MAX_MB,
  POLICY_PHOTOS_PER_FIELD,
  POLICY_PHOTOS_PER_NOTE,
} from "../lib/constants";
import { defaultFieldName } from "../lib/fieldName";
import { todayIso } from "../lib/format";

export type QueueStatus = "queued" | "running" | "done" | "error";

export interface QueueEntry {
  fieldId: number;
  fieldName: string;
  status: QueueStatus;
  runId: number | null;
  error: string | null;
}

export type RunPhase = "idle" | "waiting" | "running" | "done" | "error";

/**
 * КТО ЗАПУСТИЛ прогон. Не косметика: консоль возвращается на вкладку «Анализ», когда прогон
 * закончился, и это право узкое — оно принадлежит прогону, который человек запустил САМ,
 * кнопкой под ответом. Пакет переключает выбранное поле на каждом шаге и заканчивается
 * двадцать раз подряд; вкладка, дёргающаяся на каждом из них, отнимала бы заметки у того, кто
 * их в это время читает.
 */
export type RunSource = "single" | "batch";

export interface RunState {
  fieldId: number | null;
  phase: RunPhase;
  events: ProgressEvent[];
  startedAt: number | null;
  error: string | null;
  source: RunSource;
}

export interface Draft {
  geometry: GeoJsonPolygon;
  areaHa: number;
  name: string;
  /**
   * The name is still the one we derived, so it keeps following the contour while the ring
   * is dragged. The first keystroke in the box ends that for good — a name somebody typed
   * must never be silently replaced by moving a vertex.
   */
  nameAuto: boolean;
}

const DEFAULT_FLAGS: RunFlags = { QA_LEGACY_GRID: true, RADAR_POWER_DOMAIN: false };

/** Пока идёт расчёт типа поля: GET отвечает 404, поэтому просто опрашиваем его. */
const FIELD_USE_POLL_MS = 1000;
/** Расчёт — десятки секунд. Дальше перестаём опрашивать, чтобы не крутиться вечно. */
const FIELD_USE_POLL_LIMIT = 60;

export type FieldUsePhase = "idle" | "loading" | "running" | "error";

export interface FieldUseState {
  phase: FieldUsePhase;
  error: string | null;
}

/**
 * Is this run's verdict still sealed?
 *
 * ONLY IN VERIFICATION MODE. Sealing is a rule of OUR measurement, not of the product: it
 * exists so the agreement number is about the model and not about our own suggestion. The
 * product's users are treasure hunters at home choosing where to drive — they never record
 * observations, so a screen that demands one before answering is answering the wrong person
 * (docs/PLAN.md §2.1). The caller passes `verificationMode` and it is checked FIRST, so the
 * product path cannot seal anything by accident. It has no default on purpose: a call site
 * that forgets it does not compile.
 *
 * Inside that mode the old rule is unchanged: recording an observation for the run unseals
 * it, and so does asking explicitly — which is what `blind_entry` then distinguishes. A
 * failed run is never sealed: it carries no verdict to bias anyone with, and sealing it
 * would only hide the reason it failed.
 */
export const isVerdictSealed = (
  run: Run | null,
  observations: GroundTruth[],
  revealedRunIds: number[],
  verificationMode: boolean,
): boolean => {
  if (!verificationMode) return false;
  if (run === null || run.status === "running") return false;
  if (run.result === null || run.result === undefined) return false;
  if (revealedRunIds.includes(run.id)) return false;
  return !observations.some((observation) => observation.run_id === run.id);
};

const errorText = (error: unknown): string => {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return T.common.unknownError;
};

/**
 * The two marker refusals that have wording of their own, named by `reason` rather than by
 * parsing a message.
 *
 * Both have to say the NUMBER: "too far" without a distance does not tell you which way to
 * move the point, and "too many" without a count does not tell you how many to delete. The
 * server's own message is English, and English does not reach the product surface
 * (правила текстов — ТЗ.md).
 */
const markerRefusal = (error: unknown, limit: number): string => {
  if (error instanceof ApiError) {
    if (error.reason === "marker_too_far") return T.markers.tooFar(POLICY_MARKER_MAX_DISTANCE_M);
    // The server's own ceiling when the list has stated one; the constant only stands in
    // before the first read, where a wrong number is better than no sentence.
    if (error.reason === "too_many_markers") {
      return T.markers.limit(Number.isFinite(limit) ? limit : POLICY_MARKERS_PER_FIELD);
    }
  }
  return errorText(error);
};

/**
 * The marker is not there any more — deleted in another tab, or from another window on the
 * same account.
 *
 * It is not a refusal and must not be printed as one: for a delete it IS the wanted outcome,
 * and for an edit the honest answer is that the pin being reworded no longer exists. Either
 * way the row goes from local state, and nothing English reaches the screen — there is no
 * Russian sentence for it because there is nothing to say.
 */
const markerVanished = (error: unknown): boolean =>
  error instanceof ApiError && error.reason === "marker_not_found";

/**
 * How a marker mutation ended, reason included.
 *
 * `markerError` state alone was not enough: it is read by the toolbar's sheet, which is drawn
 * only while the placing mode is on, and it has not re-rendered yet at the moment the caller's
 * `await` returns. The popup that has to print the refusal therefore gets it in hand.
 */
export interface MarkerOutcome {
  ok: boolean;
  message: string | null;
  /**
   * Id только что созданной заметки. Заполняется ровно на одном пути — POST черновика, — и
   * ждут его файлы, накопленные в попапе до сохранения: отправить их было некуда, пока
   * заметки не существовало.
   */
  markerId?: number;
}

/**
 * The photo refusals that have wording of their own, named by `reason` like the markers'.
 *
 * EVERY ONE OF THEM QUOTES A NUMBER OR A NAME, and for the same reason as there: "too large"
 * without the ceiling does not say what to do with the next file, and in a batch of twenty a
 * refusal without the file name does not say which one was refused. `storage_full` is the odd
 * one — it names nothing because there is nothing the user can do about our disk, and the
 * sentence deliberately does not invite a retry.
 */
const photoRefusal = (
  error: unknown,
  file: { name: string; size: number },
  limit: number,
  notesLimit: number,
): string => {
  if (error instanceof ApiError) {
    /**
     * ПОТОЛОК ЗАМЕТОК — ТЕПЕРЬ ОТКАЗ ФАЙЛА, а не тихий исход (notes-v2). Раньше кадру,
     * которому некуда встать, просто не давали точки и принимали его; теперь фотографии вне
     * заметки не существует, класть некуда, и файл не загружен вовсе. Поэтому строка говорит
     * и это, и то, что освобождать надо ЗАМЕТКУ, а не фотографию.
     */
    if (error.reason === "too_many_markers") {
      const stated = error.detailNumber("limit");
      if (stated !== null) return T.photos.notesQuotaFull(stated);
      return T.photos.notesQuotaFull(
        Number.isFinite(notesLimit) ? notesLimit : POLICY_MARKERS_PER_FIELD,
      );
    }
    /**
     * ВТОРОЙ ПОТОЛОК, И ОН НЕ ПРО ПОЛЕ. На поле места ещё сколько угодно — кончилось оно у
     * этой заметки, и чистить надо здесь или завести новую. Не сказать этого значит отправить
     * человека удалять кадры не с той карточки.
     */
    if (error.reason === "too_many_photos_note") {
      const stated = error.detailNumber("limit");
      return T.photos.limitNote(stated ?? POLICY_PHOTOS_PER_NOTE);
    }
    if (error.reason === "too_many_photos") {
      /**
       * ЧИСЛО ИЗ САМОГО ОТКАЗА, и только если его там нет — из списка, и только если нет и
       * его — константа.
       *
       * Сработавший потолок называет `detail.limit` (api/errors.py `too_many_photos`), и это
       * единственное число, про которое точно известно, что оно остановило ИМЕННО эту
       * загрузку. `limit` из тела `GET /photos` прочитан раньше и мог устареть — а предел,
       * который сервер только что применил, и предел, названный в сообщении, обязаны
       * совпадать. Потолок аккаунта, из-за которого это число когда-то было брать нельзя,
       * удалён вместе со своим отказом (решение владельца, unified-notes B4).
       */
      const stated = error.detailNumber("limit");
      if (stated !== null) return T.photos.limit(stated);
      return T.photos.limit(Number.isFinite(limit) ? limit : POLICY_PHOTOS_PER_FIELD);
    }
    if (error.reason === "unsupported_image") return T.photos.notImage(file.name);
    if (error.reason === "photo_too_large") {
      return T.photos.tooLarge(file.name, Math.round(file.size / 1_000_000), POLICY_PHOTO_MAX_MB);
    }
    if (error.reason === "storage_full") return T.photos.storageFull;
  }
  // Not `errorText`: the transport's own words are English, and an upload that failed says
  // the same thing whether the cause was a timeout or a 500 — this one did not land.
  return T.photos.uploadFailed(file.name);
};

/**
 * The two refusals worth spending a round trip to avoid.
 *
 * Neither replaces the server's check — it counts the bytes as it reads them and decodes the
 * image rather than trusting a header, which is the check that actually holds. This one saves
 * pushing 40 MB up a mobile connection to be told no, and it is the only refusal in the block
 * that can appear without the network.
 *
 * AN EMPTY `type` IS NOT A REFUSAL. Browsers leave it blank for HEIC often enough that
 * refusing on it would reject the format the strings promise to accept; the server decodes
 * the content and is the one that can tell.
 */
const localPhotoRefusal = (file: File): string | null => {
  if (file.type !== "" && !file.type.startsWith("image/")) return T.photos.notImage(file.name);
  if (file.size > POLICY_PHOTO_MAX_MB * 1_000_000) {
    return T.photos.tooLarge(file.name, Math.round(file.size / 1_000_000), POLICY_PHOTO_MAX_MB);
  }
  return null;
};

/** The photo is not there any more. Same rule as `markerVanished`: not a sentence, a removal. */
const photoVanished = (error: unknown): boolean =>
  error instanceof ApiError && error.reason === "photo_not_found";

export type PhotoUploadStatus = "queued" | "uploading" | "done" | "error";

/**
 * One file on its way up. The queue is sequential (one request in flight), which is not a
 * simplification: the server re-encodes every upload and lets only two through at a time, so
 * a parallel fan-out of twenty would spend twenty mobile connections to wait in the same line.
 *
 * `id` is local and monotonic. A file has no server id until it lands, and two files chosen in
 * one gesture may share a name — so neither of those can key a row.
 */
export interface PhotoUpload {
  id: number;
  fieldId: number;
  file: File;
  status: PhotoUploadStatus;
  /** Why not, in Russian, already chosen by `photoRefusal`. */
  error: string | null;
  /**
   * В КАКУЮ ЗАМЕТКУ класть — сказано человеком. Есть у файлов, выбранных в карточке заметки
   * или в её попапе; у файлов из шапки панели его нет, и тогда место решает сервер по
   * координатам из EXIF.
   */
  markerId?: number;
  /**
   * КУДА СКЛАДЫВАТЬ БЕЗМЕСТНЫЕ — заметка ЭТОЙ пачки. Проставляется не человеком, а самой
   * очередью: первый файл без годного места заводит заметку поездки, сервер называет её id,
   * и он же уезжает со всеми следующими. Сервер читает это поле только в той ветке.
   */
  fallbackMarkerId?: number;
  /**
   * Что сервер сделал с файлом. `null` — ещё не отвечал; после ответа строка очереди говорит
   * про место, а `explicit` молчит: человек сам назвал заметку и уже знает.
   */
  placement: PhotoPlacement | null;
  /** Почему файл попал в заметку поездки. Читается только при `batch_*`. */
  cause: PhotoPlacementCause | null;
}

/**
 * Что напечатать в строке очереди про место фотографии.
 *
 * ТРИ ОТВЕТА И МОЛЧАНИЕ. «Поставлено на карту» — у файла было годное место, и точка на карте
 * появилась. Две строки про заметку поездки — места не было, и разница между ними та, которую
 * человек проверяет глазами: «фото не помнит места» — вопрос к телефону, «снято далеко» —
 * вопрос к тому, где стоял. Молчит очередь ровно на `explicit`: заметку назвал сам человек,
 * и сообщать ему то, что он только что сделал, незачем.
 *
 * Создана заметка поездки этим файлом или уже стояла (`batch_created` против
 * `batch_attached`) — разница серверная: человек в обоих случаях получает одну заметку.
 */
export const placementLine = (
  placement: PhotoPlacement | null | undefined,
  cause: PhotoPlacementCause | null | undefined,
): string | null => {
  // Приложено к существующей заметке или заведена новая — для человека это одно и то же:
  // точка на карте появилась там, где её не было видно.
  if (placement === "attached" || placement === "created") return T.photos.placedOnMap;
  if (placement === "batch_created" || placement === "batch_attached") {
    return cause === "far" ? T.photos.batchFar : T.photos.batchNoFix;
  }
  return null;
};

export interface PhotosState {
  phase: "loading" | "idle" | "error";
  error: string | null;
}

export const useWorkspace = () => {
  const [fields, setFields] = useState<Field[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [fieldsError, setFieldsError] = useState<string | null>(null);
  /**
   * Is the archive open? It changes the REQUEST: closed, archived rows are not in `fields`
   * at all, which is the cheapest way to keep them off the map and out of a batch.
   *
   * OPEN, THEY ARE IN `fields`, AND THAT IS WHAT `liveFields` IS FOR. The first version of
   * this said the request alone was the promise, and it was not: with the archive open an
   * archived row went to the map layer, to the search box, into «Выбрать все» and into the
   * console, where «Посмотреть поле» offered work the server answers with 409. Everything
   * that ACTS on a field reads `liveFields`; only the roster, which has to draw the archive,
   * reads `fields`.
   */
  const [showArchived, setShowArchived] = useState(false);

  const [selectedFieldId, setSelectedFieldId] = useState<number | null>(null);
  const [checkedFieldIds, setCheckedFieldIds] = useState<number[]>([]);

  const [runsByField, setRunsByField] = useState<Record<number, Run[]>>({});
  const [activeRunId, setActiveRunId] = useState<number | null>(null);
  const [runState, setRunState] = useState<RunState>({
    fieldId: null,
    phase: "idle",
    events: [],
    startedAt: null,
    error: null,
    source: "single",
  });
  const [queue, setQueue] = useState<QueueEntry[]>([]);

  const [flags, setFlags] = useState<RunFlags>(DEFAULT_FLAGS);
  const [serverFlags, setServerFlags] = useState<ServerFlags | null>(null);
  const [analysisDate, setAnalysisDate] = useState<string>(todayIso());

  const [draft, setDraft] = useState<Draft | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [groundTruth, setGroundTruth] = useState<Record<number, GroundTruth[]>>({});
  /** Тип поля по полям. `null` = считалось и ответа нет, `undefined` = ещё не спрашивали. */
  const [fieldUse, setFieldUse] = useState<Record<number, FieldUse | null>>({});
  const [fieldUseState, setFieldUseState] = useState<Record<number, FieldUseState>>({});
  /** Bumped whenever a verdict lands, so the reveal animation replays once. */
  const [revealToken, setRevealToken] = useState(0);
  /**
   * VERIFICATION MODE ONLY. Runs whose verdict the operator is allowed to see: in that mode
   * a finished run starts sealed, and is unsealed by recording an observation for it or by
   * asking explicitly (which is why `blind_entry` exists). In the product mode nothing is
   * ever sealed, so this list stays empty and unused.
   */
  const [revealedRunIds, setRevealedRunIds] = useState<number[]>([]);
  /** True while the sealed ground-truth form is open, so the map hides the preview too. */
  const [blindOpen, setBlindOpen] = useState(false);

  /**
   * Hand-placed markers, by field. Only the SELECTED field's are ever drawn (v1), so they
   * are fetched on selection and kept: coming back to a field must not cost the request
   * again, and twenty points is nothing to hold.
   *
   * Their refusals do NOT go to `actionError`: that one is read by the roster and the
   * console, and a marker is placed on the map with the toolbar in the corner. A rejection
   * printed 1000 px from the click is the defect the roster already had once.
   */
  const [markers, setMarkers] = useState<Record<number, FieldMarker[]>>({});
  const [markerError, setMarkerError] = useState<string | null>(null);
  /**
   * The server's own per-field ceiling, as stated by the list response. Infinite until it
   * has said one: the alternative is a client-side guess that could shut the button on a
   * field the server would have accepted.
   */
  const [markerLimit, setMarkerLimit] = useState(Number.POSITIVE_INFINITY);
  /** Как прошёл `GET /markers` по полям. `undefined` — ещё не спрашивали. */
  const [markersState, setMarkersState] = useState<Record<number, PhotosState>>({});

  /**
   * Photographs, by field, on the same terms as the markers: the SELECTED field's are fetched
   * on selection and kept, because coming back to a field must not cost the request again.
   *
   * The upload queue is NOT keyed by field. It is sequential and it is one queue: a batch
   * chosen on one field is emptied before anything else is chosen, and each row carries its
   * own `fieldId` so a selection that moves mid-upload cannot re-address the file in flight.
   */
  const [photos, setPhotos] = useState<Record<number, FieldPhoto[]>>({});
  const [photosState, setPhotosState] = useState<Record<number, PhotosState>>({});
  const [photoLimit, setPhotoLimit] = useState(Number.POSITIVE_INFINITY);
  /**
   * Потолок фотографий НА ЗАМЕТКУ, как его назвал сервер. Отдельно от потолка поля, потому
   * что это про другое место: им гасится приём файлов в карточке, а не кнопка панели.
   */
  const [notePhotoLimit, setNotePhotoLimit] = useState(Number.POSITIVE_INFINITY);
  const [photoUploads, setPhotoUploads] = useState<PhotoUpload[]>([]);

  // Reset on mount, not just set on unmount: StrictMode mounts twice in dev, and without the
  // reset the first teardown latches `cancelled` forever, so every fetch resolves into a
  // discarded result and the app renders empty while the network tab looks perfect.
  const cancelled = useRef(false);
  useEffect(() => {
    cancelled.current = false;
    return () => {
      cancelled.current = true;
    };
  }, []);

  // --- fields -------------------------------------------------------------

  const loadFields = useCallback(async (): Promise<Field[]> => {
    setFieldsLoading(true);
    setFieldsError(null);
    try {
      const loaded = await api.listFields({ includeArchived: showArchived });
      if (cancelled.current) return loaded;
      setFields(loaded);
      // The selection has to survive a reload and NOT survive the field leaving the list:
      // archiving the field you are looking at used to leave the console pointing at an id
      // that is no longer there, which renders as "поле не выбрано" with a field selected.
      // It falls back to a LIVE field: an archived one cannot be run, watched or previewed,
      // so seeding the console with it would open the screen on a field that refuses every
      // button on it — and with the archive open a priority-2 archived row sorts first.
      const runnable = loaded.filter((field) => !field.archived_at);
      setSelectedFieldId((current) =>
        current !== null && runnable.some((field) => field.id === current)
          ? current
          : (runnable[0]?.id ?? null),
      );
      setCheckedFieldIds((current) =>
        current.filter((id) => runnable.some((field) => field.id === id)),
      );
      return loaded;
    } catch (error) {
      if (!cancelled.current) setFieldsError(errorText(error));
      return [];
    } finally {
      if (!cancelled.current) setFieldsLoading(false);
    }
  }, [showArchived]);

  useEffect(() => {
    void loadFields();
  }, [loadFields]);

  useEffect(() => {
    // Server flag defaults: what a run would use if we changed nothing.
    void api
      .getServerFlags()
      .then((loaded) => {
        if (cancelled.current) return;
        setServerFlags(loaded);
        setFlags({
          QA_LEGACY_GRID: Boolean(loaded.QA_LEGACY_GRID),
          RADAR_POWER_DOMAIN: Boolean(loaded.RADAR_POWER_DOMAIN),
        });
      })
      .catch(() => {
        // Optional endpoint: the stage-1 defaults are the notebook's, so keep them.
      });
  }, []);

  /**
   * The fields anything may ACT on. Same list as `fields` while the archive is closed.
   *
   * Everything that costs, promises or draws reads this one — the map layer, the search box,
   * «Выбрать все», the console. `fields` is for the roster alone, because the roster is the
   * one surface that has to show the archive in order to offer a way out of it.
   */
  const liveFields = useMemo(() => fields.filter((field) => !field.archived_at), [fields]);

  const selectedField = useMemo(
    () => liveFields.find((field) => field.id === selectedFieldId) ?? null,
    [liveFields, selectedFieldId],
  );

  // --- тип поля (ячейка 1) -------------------------------------------------
  //
  // Стоит выше прогонов сознательно: прогон сам запускает классификацию, когда закончился,
  // поэтому `computeFieldUse` обязан быть объявлен раньше `runOne`.

  const setUseState = useCallback((fieldId: number, next: FieldUseState) => {
    setFieldUseState((current) => ({ ...current, [fieldId]: next }));
  }, []);

  /** Прочитать кэш. 404 приходит как `null` — это норма, а не ошибка. */
  const loadFieldUse = useCallback(
    async (fieldId: number): Promise<FieldUse | null> => {
      setUseState(fieldId, { phase: "loading", error: null });
      try {
        const loaded = await api.getFieldUse(fieldId);
        if (cancelled.current) return loaded;
        setFieldUse((current) => ({ ...current, [fieldId]: loaded }));
        setUseState(fieldId, { phase: "idle", error: null });
        return loaded;
      } catch (error) {
        if (!cancelled.current) setUseState(fieldId, { phase: "error", error: errorText(error) });
        return null;
      }
    },
    [setUseState],
  );

  useEffect(() => {
    if (selectedFieldId === null) return;
    if (fieldUse[selectedFieldId] !== undefined) return;
    if (fieldUseState[selectedFieldId] !== undefined) return;
    void loadFieldUse(selectedFieldId);
  }, [selectedFieldId, fieldUse, fieldUseState, loadFieldUse]);

  /**
   * Запустить расчёт типа поля и дождаться его.
   *
   * POST отвечает 202 и считает в фоне. Пока не готово, GET отдаёт 404, поэтому ждём
   * опросом, а не выдуманным прогрессом. Повторный POST по тому же полю сервер подавляет
   * сам (`already_running`), так что двойной вызов ничего не стоит.
   */
  const computeFieldUse = useCallback(
    async (fieldId: number): Promise<FieldUse | null> => {
      setActionError(null);
      setUseState(fieldId, { phase: "running", error: null });
      try {
        await api.startFieldUse(fieldId);
        for (let attempt = 0; attempt < FIELD_USE_POLL_LIMIT; attempt += 1) {
          await new Promise((resolve) => setTimeout(resolve, FIELD_USE_POLL_MS));
          if (cancelled.current) return null;
          const loaded = await api.getFieldUse(fieldId);
          if (loaded !== null && loaded.job_state !== "running") {
            setFieldUse((current) => ({ ...current, [fieldId]: loaded }));
            setUseState(fieldId, { phase: "idle", error: null });
            void loadFields();
            return loaded;
          }
        }
        setUseState(fieldId, { phase: "error", error: T.fieldUse.timedOut });
        return null;
      } catch (error) {
        const message = errorText(error);
        if (!cancelled.current) {
          setUseState(fieldId, { phase: "error", error: message });
          setActionError(message);
        }
        return null;
      }
    },
    [loadFields, setUseState],
  );

  // Read by the run callback so it sees the freshest cache without being re-created on
  // every classification — in a batch that would restart the queue's closure mid-flight.
  const fieldUseRef = useRef(fieldUse);
  fieldUseRef.current = fieldUse;

  // --- runs ---------------------------------------------------------------

  const loadRuns = useCallback(async (fieldId: number): Promise<void> => {
    try {
      const runs = await api.listRuns(fieldId);
      if (cancelled.current) return;
      setRunsByField((current) => ({ ...current, [fieldId]: runs }));
    } catch (error) {
      if (!cancelled.current) setActionError(errorText(error));
    }
  }, []);

  useEffect(() => {
    if (selectedFieldId === null) return;
    if (runsByField[selectedFieldId] === undefined) void loadRuns(selectedFieldId);
    void (async () => {
      if (groundTruth[selectedFieldId] !== undefined) return;
      try {
        const observations = await api.listGroundTruth(selectedFieldId);
        if (!cancelled.current) {
          setGroundTruth((current) => ({ ...current, [selectedFieldId]: observations }));
        }
      } catch {
        // Not fatal: the form still works, the "already recorded" count just stays unknown.
      }
    })();
  }, [selectedFieldId, runsByField, groundTruth, loadRuns]);

  const runsOfSelected = useMemo(
    () => (selectedFieldId === null ? [] : (runsByField[selectedFieldId] ?? [])),
    [selectedFieldId, runsByField],
  );

  const activeRun = useMemo(() => {
    if (runsOfSelected.length === 0) return null;
    if (activeRunId !== null) {
      const found = runsOfSelected.find((run) => run.id === activeRunId);
      if (found) return found;
    }
    return runsOfSelected.find((run) => run.status !== "running") ?? runsOfSelected[0] ?? null;
  }, [runsOfSelected, activeRunId]);

  const revealRun = useCallback((runId: number) => {
    setRevealedRunIds((current) => (current.includes(runId) ? current : [...current, runId]));
  }, []);

  const loadRunRef = useRef<(runId: number) => Promise<void>>(async () => {});

  // GET /api/fields/{id}/runs answers without the payload, and the payload IS the evidence.
  // The active run is therefore re-read once, in full.
  useEffect(() => {
    if (activeRun === null || activeRun.status === "running") return;
    if (activeRun.payload !== undefined && activeRun.payload !== null) return;
    void loadRunRef.current(activeRun.id);
  }, [activeRun]);

  /**
   * The product surface until the server says otherwise. `serverFlags` arrives one tick
   * after mount, and the default has to be the SAFE one: booting into the sealed screen and
   * then unsealing it would flash the operator's choreography at every user.
   */
  const verificationMode = serverFlags?.VERIFICATION_MODE === true;

  const verdictSealed = useMemo(
    () =>
      isVerdictSealed(
        activeRun,
        activeRun === null ? [] : (groundTruth[activeRun.field_id] ?? []),
        revealedRunIds,
        verificationMode,
      ),
    [activeRun, revealedRunIds, groundTruth, verificationMode],
  );

  /** Re-read one run WITH its payload (the list endpoint omits it). */
  const loadRun = useCallback(async (runId: number): Promise<void> => {
    try {
      const run = await api.getRun(runId);
      if (cancelled.current) return;
      setRunsByField((current) => {
        const existing = current[run.field_id] ?? [];
        return {
          ...current,
          [run.field_id]: [run, ...existing.filter((item) => item.id !== run.id)],
        };
      });
    } catch (error) {
      if (!cancelled.current) setActionError(errorText(error));
    }
  }, []);
  loadRunRef.current = loadRun;

  const runOne = useCallback(
    async (
      fieldId: number,
      options: { flags?: RunFlags; date?: string; source?: RunSource } = {},
    ): Promise<Run | null> => {
      setActionError(null);
      setRunState({
        fieldId,
        phase: "waiting",
        events: [],
        startedAt: Date.now(),
        error: null,
        source: options.source ?? "single",
      });
      try {
        const run = await api.runField(
          fieldId,
          { analysis_date: options.date ?? analysisDate, flags: options.flags ?? flags },
          {
            onProgress: (events) => {
              if (cancelled.current) return;
              setRunState((current) =>
                current.fieldId === fieldId
                  ? { ...current, phase: events.length > 0 ? "running" : current.phase, events }
                  : current,
              );
            },
          },
        );
        if (cancelled.current) return run;
        setRunsByField((current) => {
          const existing = current[fieldId] ?? [];
          return {
            ...current,
            [fieldId]: [run, ...existing.filter((item) => item.id !== run.id)],
          };
        });
        setActiveRunId(run.id);
        setRunState((current) =>
          current.fieldId === fieldId
            ? {
                ...current,
                phase: run.status === "failed" ? "error" : "done",
                events: run.progress ?? run.payload?.progress ?? current.events,
                error: run.error,
              }
            : current,
        );
        setRevealToken((token) => token + 1);
        void loadFields();
        // Часть 1 — свойство поля, а не решение оператора: считаем сами, один раз, после
        // прогона. Именно после, а не на выбор поля: POST /field-use отвечает 409, пока
        // прогон в полёте, а `runBatch` переключает выбранное поле на каждом шаге.
        if (run.status === "succeeded" && fieldUseRef.current[fieldId] == null) {
          void computeFieldUse(fieldId);
        }
        return run;
      } catch (error) {
        if (!cancelled.current) {
          const message = errorText(error);
          setRunState((current) =>
            current.fieldId === fieldId ? { ...current, phase: "error", error: message } : current,
          );
          setActionError(message);
        }
        return null;
      }
    },
    [analysisDate, computeFieldUse, flags, loadFields],
  );

  const runBatch = useCallback(
    async (fieldIds: number[]): Promise<void> => {
      const entries: QueueEntry[] = fieldIds.map((fieldId) => ({
        fieldId,
        fieldName: fields.find((field) => field.id === fieldId)?.name ?? `#${fieldId}`,
        status: "queued",
        runId: null,
        error: null,
      }));
      setQueue(entries);
      for (const entry of entries) {
        if (cancelled.current) return;
        setSelectedFieldId(entry.fieldId);
        setQueue((current) =>
          current.map((item) =>
            item.fieldId === entry.fieldId ? { ...item, status: "running" } : item,
          ),
        );
        // `source: "batch"` — вот отсюда консоль и узнаёт, что вкладку трогать не надо.
        const run = await runOne(entry.fieldId, { source: "batch" });
        setQueue((current) =>
          current.map((item) =>
            item.fieldId === entry.fieldId
              ? {
                  ...item,
                  status: run && run.status !== "failed" ? "done" : "error",
                  runId: run?.id ?? null,
                  error: run?.error ?? null,
                }
              : item,
          ),
        );
      }
    },
    [fields, runOne],
  );

  const clearQueue = useCallback(() => setQueue([]), []);

  // --- drawing and import ------------------------------------------------

  const setDraftGeometry = useCallback((geometry: GeoJsonPolygon | null) => {
    setDraft((current) => {
      if (!geometry) return null;
      const typed = current !== null && !current.nameAuto;
      return {
        geometry,
        areaHa: areaHectares(geometry),
        // Filled in the moment the ring closes, so nobody reaches «Сохранить» past an empty
        // box. Derived from where the field IS, because a default keyed on the day collides
        // with every other contour drawn that day (lib/fieldName.ts).
        name: typed ? current.name : defaultFieldName(geometry),
        nameAuto: !typed,
      };
    });
  }, []);

  const setDraftName = useCallback((name: string) => {
    setDraft((current) => (current ? { ...current, name, nameAuto: false } : current));
  }, []);

  const saveDraft = useCallback(async (): Promise<Field | null> => {
    if (!draft) return null;
    setActionError(null);
    try {
      const created = await api.createField({
        name: draft.name.trim() || defaultFieldName(draft.geometry),
        geometry: draft.geometry,
        source: "draw",
      });
      setFields((current) => [created, ...current]);
      setSelectedFieldId(created.id);
      setDraft(null);
      return created;
    } catch (error) {
      setActionError(errorText(error));
      return null;
    }
  }, [draft]);

  const importFeatures = useCallback(
    async (features: ImportedFeature[]): Promise<{ created: number; failed: string[] }> => {
      const failed: string[] = [];
      let created = 0;
      for (const [index, feature] of features.entries()) {
        try {
          const field = await api.createField({
            name: feature.name || T.import.defaultFieldName(index + 1),
            geometry: feature.geometry,
            source: "import",
          });
          created += 1;
          setFields((current) => [field, ...current]);
          setSelectedFieldId((currentId) => currentId ?? field.id);
        } catch (error) {
          failed.push(`${feature.name ?? `#${index + 1}`}: ${errorText(error)}`);
        }
      }
      return { created, failed };
    },
    [],
  );

  /**
   * Rename, re-prioritise, annotate or (un)archive. One call for all four, because the
   * server has one endpoint for all four.
   *
   * The list is re-read afterwards rather than patched in place: the ORDER is the server's
   * (priority first), and an archived field has to leave the roster. Patching the row
   * locally would leave the list sorted by whatever it was sorted by before the change —
   * which is exactly the state the priority mark exists to fix.
   *
   * ONE REFUSAL IS ANSWERED BY NAME. Coming back from the archive costs a quota slot again,
   * so on a full account it fails — and `errorText` would have printed the server's English
   * ("account is limited to 20 fields and already owns 20") straight onto the product
   * surface, which `(правила текстов — ТЗ.md)` §6.3 forbids outright. The Russian line says the
   * same number and names the way out.
   */
  const updateField = useCallback(
    async (fieldId: number, patch: FieldPatch): Promise<Field | null> => {
      setActionError(null);
      try {
        const updated = await api.updateField(fieldId, patch);
        setFields((current) => current.map((field) => (field.id === fieldId ? updated : field)));
        void loadFields();
        return updated;
      } catch (error) {
        const quotaFull = error instanceof ApiError && error.reason === "too_many_fields";
        setActionError(quotaFull ? T.fieldCard.quotaFull : errorText(error));
        return null;
      }
    },
    [loadFields],
  );

  /**
   * Delete a field for good. Answers WHETHER it went, because the caller has something to say
   * either way: the roster prints «Поле удалено» on success and leaves the refusal under the
   * button that was pressed on failure, and neither of those is derivable from a `void`.
   */
  const removeField = useCallback(async (fieldId: number): Promise<boolean> => {
    setActionError(null);
    try {
      await api.deleteField(fieldId);
      setFields((current) => current.filter((field) => field.id !== fieldId));
      setCheckedFieldIds((current) => current.filter((id) => id !== fieldId));
      setSelectedFieldId((current) => (current === fieldId ? null : current));
      return true;
    } catch (error) {
      setActionError(errorText(error));
      return false;
    }
  }, []);

  // --- ground truth -------------------------------------------------------

  const addGroundTruth = useCallback(
    async (fieldId: number, input: GroundTruthInput): Promise<GroundTruth | null> => {
      try {
        const created = await api.createGroundTruth(fieldId, input);
        setGroundTruth((current) => ({
          ...current,
          [fieldId]: [created, ...(current[fieldId] ?? [])],
        }));
        // The observation exists now, so the verdict of the run it was entered against may
        // be shown. Nothing else unseals it.
        if (created.run_id !== null) revealRun(created.run_id);
        return created;
      } catch (error) {
        setActionError(errorText(error));
        return null;
      }
    },
    [revealRun],
  );

  // СЛЕЖЕНИЕ УБРАНО 2026-10-02 (решение владельца: «сбивает с толку, пока нет подписок»).
  // Серверные ручки живы, но отсюда их не зовёт никто: статус по полям, флаг ожидания и
  // переключатель удалены вместе с кнопкой. Держать состояние, которое никто не показывает,
  // значит каждый выбор поля платить запросом за ответ, который некуда вывести.

  // --- markers on a field --------------------------------------------------

  /**
   * ОТКАЗ СПИСКА ЗАМЕТОК ТЕПЕРЬ ЗАПИСЫВАЕТСЯ, а не глотается.
   *
   * Он глотался, пока заметка была аннотацией на карте: поле, вердикт и снимок читаются без
   * неё, и баннер был бы шумом. С notes-v2 у заметок своя панель, и молчаливый отказ обернулся
   * ложью — панель печатала «Заметок нет», то есть утверждала отсутствие там, где мы просто не
   * спросили. Хуже того, перечитать было нечем: эффект загрузки сторожит `markers[fieldId] !==
   * undefined`, а после отказа там по-прежнему `undefined` — и он не выстреливал сам, и кнопки
   * «Ещё раз» у этого списка не было.
   *
   * Состояние отдельное от фотографий: упасть может любой из двух запросов, и §6.3 требует
   * одного ответа на оба — отказ с «Ещё раз», перечитывающим ОБА списка.
   */
  const loadMarkers = useCallback(async (fieldId: number): Promise<void> => {
    setMarkersState((current) => ({ ...current, [fieldId]: { phase: "loading", error: null } }));
    try {
      const loaded = await api.listMarkers(fieldId);
      if (cancelled.current) return;
      setMarkers((current) => ({ ...current, [fieldId]: loaded.markers }));
      setMarkerLimit(loaded.limit);
      setMarkersState((current) => ({ ...current, [fieldId]: { phase: "idle", error: null } }));
    } catch (error) {
      if (cancelled.current) return;
      setMarkersState((current) => ({
        ...current,
        [fieldId]: { phase: "error", error: errorText(error) },
      }));
    }
  }, []);

  // Read by the pump, which must not be re-created when this function is.
  const loadMarkersRef = useRef(loadMarkers);
  loadMarkersRef.current = loadMarkers;

  useEffect(() => {
    if (selectedFieldId === null) return;
    if (markers[selectedFieldId] !== undefined) return;
    // Сторожит и СОСТОЯНИЕ тоже: без него отказ оставлял `markers[fieldId]` пустым, эффект
    // видел «не загружено» и уходил в бесконечный круг запросов по падающему серверу.
    if (markersState[selectedFieldId] !== undefined) return;
    void loadMarkersRef.current(selectedFieldId);
  }, [selectedFieldId, markers, markersState]);

  const markersOfSelected = useMemo(
    () => (selectedFieldId === null ? [] : (markers[selectedFieldId] ?? [])),
    [selectedFieldId, markers],
  );

  /**
   * At the cap the button is shut rather than the twenty-first click refused.
   *
   * `false` until the list has been read: the ceiling is the SERVER's number, and guessing
   * it from a constant would shut the button on a field whose markers have not loaded yet.
   */
  const markersAtLimit = markersOfSelected.length >= markerLimit;

  const addMarker = useCallback(
    async (fieldId: number, input: FieldMarkerInput): Promise<MarkerOutcome> => {
      setMarkerError(null);
      try {
        const created = await api.createMarker(fieldId, input);
        setMarkers((current) => ({
          ...current,
          [fieldId]: [...(current[fieldId] ?? []), created],
        }));
        // Id уезжает наверх: файлы, накопленные в попапе черновика, ждали ровно его.
        return { ok: true, message: null, markerId: created.id };
      } catch (error) {
        const message = markerRefusal(error, markerLimit);
        setMarkerError(message);
        return { ok: false, message };
      }
    },
    [markerLimit],
  );

  /**
   * Заметки больше нет — ни в нашей модели, ни на сервере.
   *
   * ФОТОГРАФИИ УХОДЯТ ВМЕСТЕ С НЕЙ, И БЕЗ ПЕРЕЧИТКИ (notes-v2). Раньше сервер снимал привязку
   * (ON DELETE SET NULL) и кадры оставались в поле сами по себе; теперь их нет — миграция 015
   * поставила каскад, и файлы сносятся после коммита. Так что удаление строк здесь не догадка
   * о будущем, а то, что на сервере уже произошло, и вопрос об удалении честно называет число
   * (`markers.deleteAsk`).
   *
   * Локально, а не через `GET /photos`: ответ известен заранее целиком, и круг к серверу ради
   * него был бы платой за то, что мы и так знаем.
   *
   * Живёт в `dropMarker`, а не в `removeMarker`, потому что заметка уходит из модели тремя
   * путями: своё удаление, `marker_not_found` при правке и он же при удалении. Снести кадры
   * надо во всех трёх — во втором и третьем её удалили из другой вкладки, но следствие то же.
   */
  const dropMarker = useCallback((fieldId: number, markerId: number): void => {
    setMarkers((current) => ({
      ...current,
      [fieldId]: (current[fieldId] ?? []).filter((marker) => marker.id !== markerId),
    }));
    setPhotos((current) => {
      const held = current[fieldId];
      if (held === undefined) return current;
      // Новый массив только если есть что менять: лишняя ссылка перезапустила бы эффект
      // загрузки миниатюр в панели и слой заметок на карте.
      if (!held.some((photo) => photo.marker_id === markerId)) return current;
      return {
        ...current,
        [fieldId]: held.filter((photo) => photo.marker_id !== markerId),
      };
    });
  }, []);

  const editMarker = useCallback(
    async (fieldId: number, markerId: number, patch: FieldMarkerPatch): Promise<MarkerOutcome> => {
      setMarkerError(null);
      try {
        const updated = await api.updateMarker(fieldId, markerId, patch);
        setMarkers((current) => ({
          ...current,
          [fieldId]: (current[fieldId] ?? []).map((marker) =>
            marker.id === markerId ? updated : marker,
          ),
        }));
        return { ok: true, message: null };
      } catch (error) {
        // The pin went away under the editor: take it off the map instead of printing the
        // server's English at somebody who cannot act on it. The popup goes with the layer,
        // so there is no sentence to carry back either.
        if (markerVanished(error)) {
          dropMarker(fieldId, markerId);
          return { ok: false, message: null };
        }
        const message = markerRefusal(error, markerLimit);
        setMarkerError(message);
        return { ok: false, message };
      }
    },
    [dropMarker, markerLimit],
  );

  const removeMarker = useCallback(
    async (fieldId: number, markerId: number): Promise<MarkerOutcome> => {
      setMarkerError(null);
      try {
        await api.deleteMarker(fieldId, markerId);
        dropMarker(fieldId, markerId);
        return { ok: true, message: null };
      } catch (error) {
        // Already gone is the outcome that was asked for, not a failure to report.
        if (markerVanished(error)) {
          dropMarker(fieldId, markerId);
          return { ok: true, message: null };
        }
        const message = markerRefusal(error, markerLimit);
        setMarkerError(message);
        return { ok: false, message };
      }
    },
    [dropMarker, markerLimit],
  );

  // --- photographs of a field ----------------------------------------------
  //
  // A calque of the markers above, with one thing markers do not have: a LOAD STATE. A pin
  // that fails to load is an annotation missing from a map that still answers every question
  // it was opened for; a photo block that fails to load is the whole block, and its own
  // heading would otherwise stand over an empty grid that reads as «фотографий нет».

  /**
   * The queue is mirrored in a ref and WRITTEN THROUGH IT.
   *
   * The pump is one `async` loop that picks the next queued row after each upload, and React
   * state inside it is a snapshot from the render the loop started in: reading it would hand
   * the loop the same row for ever. Every write goes through here so the ref and the state
   * can never disagree about what is left to send.
   */
  const uploadsRef = useRef<PhotoUpload[]>([]);
  const nextUploadId = useRef(0);
  const pumping = useRef(false);
  /**
   * Поля, чьи заметки сервер мог завести сам, пока шла загрузка.
   *
   * ОДИН ПЕРЕЧИТ НА ПАЧКУ, А НЕ НА ФАЙЛ. Кластеризация по GPS живёт на сервере: двадцать
   * кадров одного угла поля могут дать одну новую заметку, а могут не дать ни одной, и узнать
   * это из ответа на фотографию нельзя — там есть `marker_id`, но нет самой заметки. Перечит
   * после каждого файла стоил бы двадцати GET на одну поездку; здесь он один, и делается,
   * когда очередь встала.
   */
  const notesDirty = useRef(new Set<number>());

  const writeUploads = useCallback((next: PhotoUpload[]): void => {
    uploadsRef.current = next;
    setPhotoUploads(next);
  }, []);

  const patchUpload = useCallback(
    (uploadId: number, patch: Partial<PhotoUpload>): void => {
      writeUploads(
        uploadsRef.current.map((row) => (row.id === uploadId ? { ...row, ...patch } : row)),
      );
    },
    [writeUploads],
  );

  const dropPhoto = useCallback((fieldId: number, photoId: number): void => {
    setPhotos((current) => ({
      ...current,
      [fieldId]: (current[fieldId] ?? []).filter((photo) => photo.id !== photoId),
    }));
  }, []);

  const loadPhotos = useCallback(async (fieldId: number): Promise<void> => {
    setPhotosState((current) => ({ ...current, [fieldId]: { phase: "loading", error: null } }));
    try {
      const loaded = await api.listPhotos(fieldId);
      if (cancelled.current) return;
      setPhotos((current) => ({ ...current, [fieldId]: loaded.photos }));
      setPhotoLimit(loaded.limit);
      setNotePhotoLimit(loaded.note_limit);
      setPhotosState((current) => ({ ...current, [fieldId]: { phase: "idle", error: null } }));
    } catch (error) {
      if (cancelled.current) return;
      // NOT `actionError`: that banner is read by the roster and the console's action panel,
      // and a photo list that did not load is answered inside its own block, next to the
      // «Ещё раз» that retries it.
      setPhotosState((current) => ({
        ...current,
        [fieldId]: { phase: "error", error: errorText(error) },
      }));
    }
  }, []);

  // Read by the selection effect so a reload does not re-create the effect and re-fire it.
  const loadPhotosRef = useRef(loadPhotos);
  loadPhotosRef.current = loadPhotos;

  /**
   * Send the queued rows, one request at a time, until none are left.
   *
   * `pumping` is the whole of the concurrency control: choosing more files while the queue
   * runs appends to it and returns, because the loop already running will reach them. Without
   * the latch a second gesture would start a second loop and both would send the same row.
   */
  const pumpUploads = useCallback(async (): Promise<void> => {
    if (pumping.current) return;
    pumping.current = true;
    /**
     * ЗАМЕТКА ПОЕЗДКИ, по одной на поле, на время этой пачки.
     *
     * Кадры без годного места (телефон не поймал или снято дальше 500 м) не должны рассыпаться
     * по двадцати одинаковым заметкам: это одна поездка, и заметка у неё одна. Первый такой
     * файл заводит её на сервере, ответ приносит `marker_id`, и он уезжает со следующими как
     * `fallback_marker_id`. Живёт в замыкании прогона, а не в состоянии: пачка кончилась —
     * цепочка кончилась вместе с ней, и следующая поездка заводит свою заметку.
     */
    const tripNote = new Map<number, number>();
    try {
      for (;;) {
        const next = uploadsRef.current.find((row) => row.status === "queued");
        if (next === undefined || cancelled.current) return;
        patchUpload(next.id, { status: "uploading", error: null });
        const fallback = next.fallbackMarkerId ?? tripNote.get(next.fieldId);
        try {
          // Даты в запросе нет вовсе: сервер читает EXIF DateTimeOriginal в том же проходе,
          // что и координаты, и знает про этот файл больше нашего. Свою дату заметка получает
          // оттуда же, а правит её человек — в карточке.
          const created = await api.uploadPhoto(next.fieldId, next.file, {
            // Назвал человек: загрузка из карточки или попапа заметки.
            marker_id: next.markerId,
            // Назвала очередь: куда складывать, если места у файла не окажется.
            fallback_marker_id: next.markerId === undefined ? fallback : undefined,
          });
          if (cancelled.current) return;
          setPhotos((current) => ({
            ...current,
            [next.fieldId]: [created, ...(current[next.fieldId] ?? [])],
          }));
          patchUpload(next.id, {
            status: "done",
            error: null,
            placement: created.placement ?? null,
            cause: created.cause ?? null,
            fallbackMarkerId: fallback,
          });
          // ЦЕПОЧКА ЗАМКНУЛАСЬ: сервер завёл заметку поездки этим файлом и назвал её id.
          // Следующие безместные кадры пачки лягут туда же, а не заведут по своей.
          if (created.placement === "batch_created" || created.placement === "batch_attached") {
            tripNote.set(next.fieldId, created.marker_id);
          }
          /**
           * ПЕРЕЧИТ НА ЛЮБОЙ УДАЧНЫЙ ФАЙЛ (UX 0.6), а не только на созданную заметку.
           *
           * Панель показывает ленты заметок, и приложенный к существующей кадр меняет её
           * карточку ровно так же, как новый — список: без перечита он не появился бы в ленте
           * до смены поля. Перечит по-прежнему ОДИН на всю пачку — он стоит в `finally`.
           */
          notesDirty.current.add(next.fieldId);
        } catch (error) {
          if (cancelled.current) return;
          patchUpload(next.id, {
            status: "error",
            error: photoRefusal(error, next.file, photoLimitRef.current, markerLimitRef.current),
          });
        }
      }
    } finally {
      pumping.current = false;
      // Очередь встала — заметки перечитываются один раз на всю пачку. В `finally`, а не
      // после цикла: выход отсюда бывает и по `return` на отменённом хуке.
      const dirty = [...notesDirty.current];
      notesDirty.current.clear();
      if (!cancelled.current) {
        for (const fieldId of dirty) void loadMarkersRef.current(fieldId);
      }
    }
  }, [patchUpload]);

  // Потолок заметок, каким его видит насос: строка отказа обязана назвать СЕРВЕРНОЕ число, а
  // пересоздавать цикл на каждый ответ списка нельзя — это сделало бы `uploadPhotos` новой
  // функцией на каждую загрузку.
  const markerLimitRef = useRef(markerLimit);
  markerLimitRef.current = markerLimit;

  // The cap as the pump sees it. The refusal sentence quotes the SERVER's ceiling, and the
  // loop must not be re-created every time the list states it — that would restart nothing
  // but would make `uploadPhotos` a new function on every load.
  const photoLimitRef = useRef(photoLimit);
  photoLimitRef.current = photoLimit;

  useEffect(() => {
    if (selectedFieldId === null) return;
    if (photos[selectedFieldId] !== undefined) return;
    if (photosState[selectedFieldId] !== undefined) return;
    void loadPhotosRef.current(selectedFieldId);
  }, [selectedFieldId, photos, photosState]);

  const photosOfSelected = useMemo(
    () => (selectedFieldId === null ? [] : (photos[selectedFieldId] ?? [])),
    [selectedFieldId, photos],
  );

  /**
   * At the cap the button is shut rather than the thirty-first click refused. `false` until
   * the list has been read: the ceiling is the SERVER's, and guessing it from the constant
   * would shut the button on a field whose photos have not arrived yet.
   */
  const photosAtLimit = photosOfSelected.length >= photoLimit;

  /**
   * Put files in the queue and start it if it is not already running.
   *
   * The two local refusals are spent HERE rather than inside the pump: a file that is not an
   * image never becomes a request, so its row goes straight to `error` and the next file is
   * not held up behind a round trip that was never going to succeed.
   */
  const uploadPhotos = useCallback(
    (fieldId: number, files: File[], markerId?: number): void => {
      if (files.length === 0) return;
      const rows: PhotoUpload[] = files.map((file) => {
        const refusal = localPhotoRefusal(file);
        return {
          id: (nextUploadId.current += 1),
          fieldId,
          file,
          status: refusal === null ? "queued" : "error",
          error: refusal,
          markerId,
          placement: null,
          cause: null,
        };
      });
      writeUploads([...uploadsRef.current, ...rows]);
      void pumpUploads();
    },
    [pumpUploads, writeUploads],
  );

  /** One row, again. The neighbours are NOT re-sent: a partial success is not rolled back. */
  const retryPhotoUpload = useCallback(
    (uploadId: number): void => {
      writeUploads(
        uploadsRef.current.map((row) =>
          row.id === uploadId
            ? { ...row, status: "queued", error: null, placement: null, cause: null }
            : row,
        ),
      );
      void pumpUploads();
    },
    [pumpUploads, writeUploads],
  );

  const dismissPhotoUploads = useCallback((): void => writeUploads([]), [writeUploads]);

  const editPhoto = useCallback(
    async (fieldId: number, photoId: number, patch: FieldPhotoPatch): Promise<MarkerOutcome> => {
      const store = (updated: FieldPhoto): void => {
        setPhotos((current) => ({
          ...current,
          [fieldId]: (current[fieldId] ?? []).map((photo) =>
            photo.id === photoId ? updated : photo,
          ),
        }));
      };
      try {
        store(await api.updatePhoto(fieldId, photoId, patch));
        return { ok: true, message: null };
      } catch (error) {
        // Gone under the editor: take it out of the grid instead of printing the server's
        // English at somebody whose next act would be to close a photo that is not there.
        if (photoVanished(error)) {
          dropPhoto(fieldId, photoId);
          return { ok: false, message: null };
        }
        /**
         * ЗАМЕТКУ, В КОТОРУЮ ПЕРЕНОСИЛИ, УДАЛИЛИ ИЗ ДРУГОЙ ВКЛАДКИ — и устарел список, из
         * которого её выбрали, а не действие.
         *
         * Повторить с `null`, как делала прежняя версия, больше НЕЛЬЗЯ: фотография вне заметки
         * не существует, сервер отвечает на `null` 422, и «отвязать» — это не мягкая посадка,
         * а второй отказ поверх первого. Поэтому обе стороны перечитываются (одной заметки не
         * стало, а её кадры ушли каскадом), а наверх уходит немой отказ: сказать по-русски
         * «заметки, в которую вы переносите, больше нет» нечем — такой строки в словаре нет, а
         * английское сообщение сервера на продуктовую поверхность не выходит. Список
         * перерисуется сам, и выбирать будут из живого.
         */
        if (markerVanished(error) && patch.marker_id !== undefined) {
          void loadMarkers(fieldId);
          void loadPhotos(fieldId);
          return { ok: false, message: null };
        }
        /**
         * ПЕРЕНОСИЛИ В ПОЛНУЮ ЗАМЕТКУ. Тот же потолок, что и у загрузки, но пришёл он на
         * PATCH: у заметки-приёмника уже десять кадров. Строка та же и по той же причине —
         * освобождать надо в ЭТОЙ заметке, а не на поле, где места сколько угодно.
         */
        if (error instanceof ApiError && error.reason === "too_many_photos_note") {
          const stated = error.detailNumber("limit");
          return { ok: false, message: T.photos.limitNote(stated ?? POLICY_PHOTOS_PER_NOTE) };
        }
        return { ok: false, message: errorText(error) };
      }
    },
    [dropPhoto, loadMarkers, loadPhotos],
  );

  const removePhoto = useCallback(
    async (fieldId: number, photoId: number): Promise<MarkerOutcome> => {
      try {
        await api.deletePhoto(fieldId, photoId);
        dropPhoto(fieldId, photoId);
        return { ok: true, message: null };
      } catch (error) {
        // Already gone is the outcome that was asked for, not a failure to report.
        if (photoVanished(error)) {
          dropPhoto(fieldId, photoId);
          return { ok: true, message: null };
        }
        return { ok: false, message: errorText(error) };
      }
    },
    [dropPhoto],
  );

  const toggleChecked = useCallback((fieldId: number) => {
    setCheckedFieldIds((current) =>
      current.includes(fieldId) ? current.filter((id) => id !== fieldId) : [...current, fieldId],
    );
  }, []);

  return {
    /** The roster's list: includes the archive when the archive is open. */
    fields,
    /** Everything that acts on a field. See the note on `showArchived`. */
    liveFields,
    fieldsLoading,
    fieldsError,
    loadFields,

    selectedFieldId,
    selectedField,
    selectField: (fieldId: number | null) => {
      setSelectedFieldId(fieldId);
      setActiveRunId(null);
    },

    checkedFieldIds,
    toggleChecked,
    // Live only: a batch that included the archive answered 409 per archived item, and the
    // one thing «Выбрать все» must never do is queue work that is refused by design.
    checkAll: () => setCheckedFieldIds(liveFields.map((field) => field.id)),
    clearChecked: () => setCheckedFieldIds([]),

    runsOfSelected,
    activeRun,
    verificationMode,
    verdictSealed,
    revealRun,
    blindOpen,
    setBlindOpen,
    loadRun,
    selectRun: setActiveRunId,
    runState,
    runOne,
    runBatch,
    queue,
    clearQueue,
    revealToken,

    flags,
    setFlags,
    serverFlags,
    analysisDate,
    setAnalysisDate,

    draft,
    setDraftGeometry,
    setDraftName,
    saveDraft,
    importFeatures,
    updateField,
    removeField,

    showArchived,
    setShowArchived,

    groundTruth,
    addGroundTruth,

    markersOfSelected,
    markersState: selectedFieldId === null ? undefined : markersState[selectedFieldId],
    markersAtLimit,
    markerLimit,
    loadMarkers,
    markerError,
    setMarkerError,
    addMarker,
    editMarker,
    removeMarker,

    photosOfSelected,
    photosState: selectedFieldId === null ? undefined : photosState[selectedFieldId],
    photosAtLimit,
    photoLimit,
    notePhotoLimit,
    photoUploads,
    loadPhotos,
    uploadPhotos,
    retryPhotoUpload,
    dismissPhotoUploads,
    editPhoto,
    removePhoto,

    fieldUse,
    fieldUseState,
    loadFieldUse,
    computeFieldUse,

    actionError,
    setActionError,
  };
};

export type Workspace = ReturnType<typeof useWorkspace>;
